<template>
  <VVVLayoutNew>
    <template #page-title> Applications </template>
    <MDBCard class="shadow-0 card-container">
      <MDBCardHeader
        class="d-flex justify-content-between align-items-center gap-3 py-4 px-0"
      >
        <MDBBtn color="primary" class="fw-bold" @click="handleArchive"
          >Archive</MDBBtn
        >
        <div class="d-flex gap-3" style="width: 30%">
          <div class="did-floating-label-content w-100">
            <select v-model="filterByAccountStatus" class="did-floating-select">
              <option
                v-for="option in accountStatusFilter"
                :key="option.value"
                :value="option.value"
              >
                {{ option.text }}
              </option>
            </select>
            <label class="did-floating-label">Filter by Account Status</label>
          </div>
          <MDBInput
            inputGroup="md"
            :formOutline="false"
            v-model="searchString"
            aria-describedby="search-addon"
            aria-label="Search"
            placeholder="Enter to Search"
            @keydown.enter="handleSearch"
          >
            <span class="input-group-text" @click="handleSearch"
              ><em class="fas fa-search"
            /></span>
          </MDBInput>
        </div>
      </MDBCardHeader>
      <MDBCardBody class="d-flex flex-column p-0">
        <div class="overflow-auto mt-4" id="table-container">
          <MDBTable hover class="align-middle">
            <thead class="table-light">
              <tr>
                <th class="d-flex gap-1 align-items-center">
                  <MDBCheckbox v-model="markAll" /> Mark All
                </th>
                <th>Date</th>
                <th>User</th>
                <th>YouTube Channel URL(s)</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="isLoading">
                <tr>
                  <td colspan="6" class="text-center">
                    <div
                      class="image-container d-flex justify-content-center align-items-center"
                    >
                      <div class="loader"></div>
                      <img
                        src="@/assets/VVV/vvv-loader.png"
                        alt="Loading image"
                      />
                    </div>
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr v-if="applicationList.data.length === 0 && !isLoading">
                  <td colspan="6" class="text-center">
                    No applications found.
                  </td>
                </tr>
                <template
                  v-else
                  v-for="(item, i) in applicationList.data"
                  :key="i"
                >
                  <VVVApplicationsTable
                    :item="item"
                    @onAction="fetchApplications"
                    :is-selected="selectedApplications.includes(item.id)"
                    @select="handleSelectApplication"
                    :openDropdownId="openDropdownId"
                    @toggleDropdown="handleToggleDropdown"
                  />
                </template>
              </template>
            </tbody>
          </MDBTable>
        </div>
        <div class="overflow-auto">
          <Pagination
            v-if="applicationList.pagination.totalPages"
            :list="applicationList.data"
            :currentPage="applicationList.pagination.currentPage"
            :totalPages="applicationList.pagination.totalPages"
            :handlePreviousPage="handlePreviousPage"
            :handleNextPage="handleNextPage"
            @update:page="onPageChange"
          />
        </div>
      </MDBCardBody>
    </MDBCard>
    <MDBToast
      v-model="successToast.show"
      id="basic-success-example"
      autohide
      position="top-right"
      appendToBody
      stacking
      width="350px"
      color="success"
      text="white"
      icon="fas fa-info-circle fa-lg me-2"
    >
      <template #title>{{ successToast.title }}</template>
      {{ successToast.message }}
    </MDBToast>
    <MDBToast
      v-model="errorToast.show"
      id="basic-danger-example"
      autohide
      position="top-right"
      appendToBody
      stacking
      width="350px"
      color="danger"
      text="white"
      icon="fas fa-exclamation-triangle fa-lg me-2"
    >
      <template #title>{{ errorToast.title }}</template>
      {{ errorToast.message }}
    </MDBToast>
  </VVVLayoutNew>
</template>

<script setup>
import { GetApplicationsList } from "@/services/Applications/GetPaginatedList";
import VVVLayoutNew from "@/views/VVVNew/VVVLayoutNew.vue";
import { useTitle } from "@vueuse/core";
import {
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBCheckbox,
  MDBInput,
  MDBTable,
  MDBToast,
} from "mdb-vue-ui-kit";
import { onMounted, ref, watch } from "vue";
import Pagination from "../../../components/Reusables/Pagination.vue";
import { PostBulkArchive } from "../../../services/Applications/PostBulkArchive";
import VVVApplicationsTable from "./VVVApplicationsTable.vue";

useTitle("Applications | Very Viral Videos");

const errorToast = ref({
  title: "Error",
  show: false,
  message: "",
});

const successToast = ref({
  title: "Success",
  show: false,
  message: "",
});

const applicationList = ref({ data: [], pagination: {} });

const markAll = ref(false);
const selectedApplications = ref([]);

const accountStatusFilter = ref([
  { value: null, text: "All" },
  { value: "Pending", text: "Pending" },
  { value: "Approved", text: "Approved" },
  { value: "Rejected", text: "Rejected" },
]);

const isLoading = ref(false);
const filterByAccountStatus = ref(accountStatusFilter.value[0].value);
const searchString = ref("");
const currentPage = ref(1);
const rowsPerPage = ref(10);

const fetchApplications = async () => {
  isLoading.value = true;

  const params = {
    SortOrder: "desc",
    SortColumn: "DateUpdated",
    RowsPerPage: rowsPerPage.value,
    PageNumber: currentPage.value,
    IsCS: false, // Filter for VVV applications (non-CS)
  };

  if (searchString.value) params.SearchString = searchString.value;
  if (filterByAccountStatus.value) {
    params.Status = filterByAccountStatus.value;
  }

  try {
    const response = await GetApplicationsList(params);

    if (response && response.data) {
      applicationList.value.data = response.data;
      applicationList.value.pagination = response.pagination || {};
    } else {
      applicationList.value.data = [];
      applicationList.value.pagination = {};
    }
  } catch (error) {
    console.error("Error fetching applications:", error);
    errorToast.value.show = true;
    errorToast.value.title = "Error";
    errorToast.value.message =
      "Failed to fetch applications. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

const handleSearch = () => {
  currentPage.value = 1;
  fetchApplications();
};

watch(filterByAccountStatus, () => {
  currentPage.value = 1;
  fetchApplications();
});

const handlePreviousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetchApplications();
  }
};

const handleNextPage = () => {
  if (currentPage.value < applicationList.value.pagination.totalPages) {
    currentPage.value++;
    fetchApplications();
  }
};

const onPageChange = (page) => {
  currentPage.value = page;
  fetchApplications();
};

watch(markAll, (checked) => {
  if (checked) {
    selectedApplications.value = applicationList.value.data.map((a) => a.id);
  } else {
    selectedApplications.value = [];
  }
});

const handleSelectApplication = (id, isSelected) => {
  if (isSelected) {
    if (!selectedApplications.value.includes(id)) {
      selectedApplications.value.push(id);
    }
  } else {
    selectedApplications.value = selectedApplications.value.filter(
      (appId) => appId !== id
    );
  }

  markAll.value =
    selectedApplications.value.length === applicationList.value.data.length;
};

const handleArchive = async () => {
  if (selectedApplications.value.length === 0) {
    console.warn("No applications selected.");
    return;
  }

  const payload = {
    applicationIds: selectedApplications.value,
  };

  try {
    const response = await PostBulkArchive(payload);
    if (response) {
      fetchApplications();
      successToast.value.show = true;
      successToast.value.title = "Success";
      successToast.value.message = "Applications archived successfully";
    } else {
      errorToast.value.show = true;
      errorToast.value.title = "Error";
      errorToast.value.message = "Failed to archive applications.";
    }
  } catch (err) {
    console.error("Error archiving applications:", err);
    errorToast.value.show = true;
    errorToast.value.title = "Error";
    errorToast.value.message = `Error archiving applications:: ${err}`;
  }
};

onMounted(() => {
  fetchApplications();
});

const openDropdownId = ref(null);

const handleToggleDropdown = (id) => {
  openDropdownId.value = openDropdownId.value === id ? null : id;
};
</script>

<style scoped>
.btn-primary {
  background-color: var(--primary);
}

.btn-outline-primary {
  border-color: var(--accent);
  color: var(--accent);
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.2), 0 2px 10px 0 rgba(0, 0, 0, 0.1);
}

.table-dark {
  --mdb-table-bg: var(--accent);
  border-color: var(--accent);
}
</style>

<style>
.user-management-loader {
  color: var(--accent);
}

th {
  text-transform: uppercase;
}

tr {
  cursor: default;
}

.card-container {
  flex: 1 1 auto;
  overflow: auto;
}

#table-container {
  flex: 1 1 auto;
  height: 60vh;
}

.image-container {
  position: relative;
  display: inline-block;
}

.image-container .loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  width: 45px;
  height: 45px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: BorderSpin 1s linear infinite;
}

.image-container img {
  display: block;
  width: 45px;
  height: 45px;
  border-radius: 50%;
}

@keyframes BorderSpin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* customized select with floating label */
.did-floating-label-content {
  position: relative;
}

.did-floating-label {
  color: #1e4c82;
  font-size: 15px;
  position: absolute;
  pointer-events: none;
  left: 8.2px;
  top: 11px;
  padding: 0 5px;
  background: #fff;
  transition: 0.2s ease all;
  -moz-transition: 0.2s ease all;
  -webkit-transition: 0.2s ease all;
}

.did-floating-input,
.did-floating-select {
  font-size: 13px;
  display: block;
  width: 100%;
  height: 34px;
  padding: 8px 13px;
  padding-right: 19px;
  background: #fff;
  color: #6c757d;
  border: 1px solid #adb5bd;
  border-radius: 4px;
  box-sizing: border-box;

  &:focus {
    outline: 1.5px solid #0d6efd;

    ~ .did-floating-label {
      color: #0d6efd;
      top: -8px;
      font-size: 11px;
    }
  }
}

select.did-floating-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

select.did-floating-select::-ms-expand {
  display: none;
}

.did-floating-input:not(:placeholder-shown) ~ .did-floating-label {
  top: -7.5px;
  font-size: 11px;
}

.did-floating-select:not([value=""]):valid ~ .did-floating-label {
  top: -7.5px;
  font-size: 11px;
}

.did-floating-select[value=""]:focus ~ .did-floating-label {
  top: 11px;
  font-size: 13px;
}

.did-floating-select:not([multiple]):not([size]) {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='6' viewBox='0 0 8 6'%3E%3Cpath id='Path_1' data-name='Path 1' d='M371,294l4,6,4-6Z' transform='translate(-371 -294)' fill='%23003d71'/%3E%3C/svg%3E%0A");
  background-position: right 15px top 50%;
  background-repeat: no-repeat;
}

.userType-width {
  width: 65%;
}

.accountStatus-width {
  width: 75%;
}

.filter-container {
  width: 60%;
}

@media (max-width: 767px) {
  .userType-width {
    width: 100%;
  }

  .accountStatus-width {
    width: 100%;
  }

  .filter-container {
    width: 100%;
  }
}
</style>
